import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Wrench, Calendar, ShieldCheck, AlertTriangle, ClipboardList } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Thumbnail } from '../../components/LightboxProvider';
import { fmtETDate } from '../../utils/tz';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_LABEL = {
  water_heater: 'Water Heater', tankless_heater: 'Tankless Heater',
  water_softener: 'Water Softener', sump_pump: 'Sump Pump',
  battery_backup: 'Battery Backup', ejector_pump: 'Ejector Pump',
  backflow_preventer: 'Backflow Preventer', sewer_line: 'Sewer Line',
  well_pump: 'Well Pump', well_tank: 'Well Tank',
  pressure_tank: 'Pressure Tank', garbage_disposal: 'Disposal',
  sewage_ejector: 'Sewage Ejector', filtration_system: 'Filtration System',
  prv: 'PRV', expansion_tank: 'Expansion Tank', recirc_pump: 'Recirculation Pump',
  main_shutoff: 'Main Shutoff', hose_bib: 'Hose Bib',
  backup_sump_pump: 'Backup Sump Pump', toilet: 'Toilet', faucet: 'Faucet',
  shower_valve: 'Shower Valve', tub_shower_fixture: 'Tub/Shower Fixture',
  kitchen_sink: 'Kitchen Sink', bathroom_sink: 'Bathroom Sink',
  laundry_box: 'Laundry Box', ice_maker_box: 'Ice Maker Box',
  dishwasher_connection: 'Dishwasher Connection', gas_line: 'Gas Line',
  floor_drain: 'Floor Drain', cleanout: 'Cleanout', water_service: 'Water Service',
  well_system: 'Well System',
  furnace: 'Furnace', ac_condenser: 'Air Conditioner / Condenser',
  evaporator_coil: 'Evaporator Coil', generator: 'Generator',
  transfer_switch: 'Transfer Switch', monitoring_station: 'Monitoring Station',
  irrigation_zone: 'Irrigation Zone',
  other: 'Other',
};

const fmt = (s) => { if (!s) return '—'; return fmtETDate(s.includes('T') ? s : s + 'T12:00:00Z'); };
const daysFromToday = (s) => { if (!s) return null; try { return Math.round((new Date(s + 'T00:00:00') - new Date()) / (1000 * 60 * 60 * 24)); } catch { return null; } };

const CustomerEquipment = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mediaByAsset, setMediaByAsset] = useState({});
  const [historyByAsset, setHistoryByAsset] = useState({});
  const [openHist, setOpenHist] = useState({});

  useEffect(() => {
    const load = async () => {
      if (!user?._id) return;
      try {
        const { data } = await axios.get(`${API}/customers/${user._id}/assets`, { withCredentials: true });
        setItems((data || []).filter((a) => a.published !== false));
        try {
          const m = await axios.get(`${API}/job-media`, { withCredentials: true, params: { customer_id: user._id } });
          const grp = {};
          (m.data || []).forEach((md) => { if (md.asset_id) { (grp[md.asset_id] = grp[md.asset_id] || []).push(md); } });
          setMediaByAsset(grp);
        } catch { /* media optional */ }
      } finally { setLoading(false); }
    };
    load();
  }, [user]);

  const toggleHistory = async (assetId) => {
    setOpenHist((o) => ({ ...o, [assetId]: !o[assetId] }));
    if (!historyByAsset[assetId]) {
      try {
        const { data } = await axios.get(`${API}/admin/assets/${assetId}/history`, { withCredentials: true });
        setHistoryByAsset((h) => ({ ...h, [assetId]: data || [] }));
      } catch { setHistoryByAsset((h) => ({ ...h, [assetId]: [] })); }
    }
  };

  return (
    <div data-testid="customer-equipment-page" className="space-y-6">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">My Equipment</h2>
        <p className="text-[#A3A3A3]">Everything Nexflow has identified or installed at your property — keep this handy for warranty claims and service calls.</p>
      </div>

      {loading ? (
        <div className="bg-[#141414] border border-[#262626] rounded-md p-8 text-center text-[#A3A3A3]">Loading...</div>
      ) : items.length === 0 ? (
        <div className="bg-[#141414] border border-[#262626] rounded-md p-10 text-center">
          <Wrench className="w-8 h-8 text-[#525252] mx-auto mb-2" />
          <p className="text-white">No equipment on file yet.</p>
          <p className="text-[#A3A3A3] text-sm mt-1">Your tech will catalog any equipment during your next visit.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((a) => {
            const dToService = daysFromToday(a.next_service_date);
            const dToWarranty = daysFromToday(a.warranty_until);
            return (
              <div key={a.id} data-testid={`cust-eq-row-${a.id}`} className="bg-[#141414] border border-[#262626] rounded-md p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Wrench className="w-4 h-4 text-[#D4AF37]" />
                      <p className="text-white font-medium">{TYPE_LABEL[a.type] || a.type}</p>
                      {a.brand && <span className="text-[#D4AF37] text-sm">{a.brand} {a.model || ''}</span>}
                    </div>
                    <div className="text-[#A3A3A3] text-xs mt-2 space-y-0.5">
                      {a.serial ? <p>Serial #: <span className="text-white">{a.serial}</span></p> : a.asset_id_native && <p>Asset ID: <span className="text-white">{a.asset_id_native}</span></p>}
                      <p className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Installed: <span className="text-white">{fmt(a.install_date)}</span></p>
                      {a.location && <p>Location: <span className="text-white">{a.location}</span></p>}
                      {a.last_service_date && <p>Last Service: <span className="text-white">{fmt(a.last_service_date)}</span></p>}
                    </div>
                  </div>
                  <div className="text-right text-xs">
                    <div className="flex items-center gap-1 justify-end mb-1">
                      <ShieldCheck className="w-3 h-3 text-green-400" />
                      <span className="text-[#A3A3A3]">Warranty: <span className={dToWarranty !== null && dToWarranty < 0 ? 'text-red-400' : 'text-white'}>{fmt(a.warranty_until)}</span></span>
                    </div>
                    <div className="flex items-center gap-1 justify-end">
                      <Calendar className="w-3 h-3 text-[#D4AF37]" />
                      <span className="text-[#A3A3A3]">Next Service: <span className={dToService !== null && dToService < 0 ? 'text-red-400' : dToService !== null && dToService <= 30 ? 'text-[#D4AF37]' : 'text-white'}>{fmt(a.next_service_date)}</span></span>
                    </div>
                    {dToService !== null && dToService < 0 && (
                      <p className="text-red-400 text-[10px] mt-1 flex items-center gap-1 justify-end"><AlertTriangle className="w-3 h-3" /> Service overdue</p>
                    )}
                  </div>
                </div>

                {/* Photos (only shown for assets approved by manager — already filtered by published=true) */}
                {Array.isArray(a.photo_urls) && a.photo_urls.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mt-3" data-testid={`cust-eq-photos-${a.id}`}>
                    {(() => {
                      const urls = a.photo_urls.map((p) => p.data_url || p.url || p).filter(Boolean);
                      return a.photo_urls.map((p, i) => (
                        <div key={i} className="bg-[#0A0A0A] border border-[#262626] rounded overflow-hidden">
                          <Thumbnail
                            src={p.data_url || p.url || p}
                            images={urls}
                            index={i}
                            alt={p.kind || 'photo'}
                            className="w-full h-24 object-cover"
                          />
                          {p.kind && <p className="text-[10px] text-[#A3A3A3] text-center py-1">{p.kind.replace('_', ' ')}</p>}
                        </div>
                      ));
                    })()}
                  </div>
                )}

                {/* Canonical job media (Before/After/Asset) — customer-visible only */}
                {(mediaByAsset[a.id] || []).length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mt-3" data-testid={`cust-eq-jobmedia-${a.id}`}>
                    {(() => {
                      const urls = (mediaByAsset[a.id] || []).map((m) => `${BACKEND_URL}${m.url}`);
                      return (mediaByAsset[a.id] || []).map((m, i) => (
                        <div key={m.id} className="bg-[#0A0A0A] border border-[#262626] rounded overflow-hidden">
                          <Thumbnail src={`${BACKEND_URL}${m.url}`} images={urls} index={i} alt={m.category}
                            className="w-full h-24 object-cover" />
                          <p className="text-[10px] text-[#A3A3A3] text-center py-1">{(m.category || '').replace('_', ' ')}</p>
                        </div>
                      ));
                    })()}
                  </div>
                )}

                {/* Service history */}
                <button type="button" onClick={() => toggleHistory(a.id)} data-testid={`cust-eq-history-toggle-${a.id}`}
                  className="text-[#D4AF37] text-xs mt-3 hover:underline">
                  {openHist[a.id] ? 'Hide' : 'View'} Service History
                </button>
                {openHist[a.id] && (
                  <div className="mt-2 space-y-1" data-testid={`cust-eq-history-${a.id}`}>
                    {(historyByAsset[a.id] || []).length === 0 ? (
                      <p className="text-[#737373] text-xs">No service history yet.</p>
                    ) : (historyByAsset[a.id] || []).map((h) => (
                      <div key={h.id} className="flex items-center gap-2 text-xs text-[#A3A3A3]">
                        <span className="text-[#D4AF37] capitalize">{(h.event || '').replace('_', ' ')}</span>
                        <span className="text-white">{fmt(h.date)}</span>
                        {h.notes && <span className="text-[#737373] truncate">— {h.notes}</span>}
                      </div>
                    ))}
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-2 mt-3 pt-3 border-t border-[#262626]">
                  <Button
                    data-testid={`cust-eq-request-service-${a.id}`}
                    size="sm"
                    onClick={() => navigate(`/customer/service-request?asset_id=${a.id}&type=service&asset_type=${a.type}`)}
                    className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"
                  >
                    <ClipboardList className="w-3.5 h-3.5 mr-1" /> Request Service
                  </Button>
                  <Button
                    data-testid={`cust-eq-schedule-${a.id}`}
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/customer/service-request?asset_id=${a.id}&type=maintenance&asset_type=${a.type}`)}
                    className="border-[#262626] text-white hover:border-[#D4AF37]"
                  >
                    <Calendar className="w-3.5 h-3.5 mr-1" /> Schedule Maintenance
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomerEquipment;
